import {
  GraduationCap,
  ScrollText,
  Building,
  Scale,
  Users,
  Award,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Seo from "@/components/Seo";
import { CAREER_TIMELINE, IMAGES, EDUCATION, BAR_ADMISSIONS, FIRM } from "@/lib/site";
import { FinalCTA } from "./Home";

const attorneyJsonLd = {
  "@context": "https://schema.org",
  "@type": "Attorney",
  name: "Joseph M. Mahaney",
  jobTitle: "Trial Attorney",
  description:
    "Massachusetts trial attorney with 40+ years of litigation experience in criminal and civil matters, including personal injury and criminal defense.",
  worksFor: { "@type": "LegalService", name: "Mahaney Law" },
  alumniOf: [
    { "@type": "CollegeOrUniversity", name: "New England School of Law" },
    { "@type": "CollegeOrUniversity", name: "University of Massachusetts Amherst" },
  ],
  areaServed: "Massachusetts",
  knowsAbout: [
    "Personal Injury",
    "Motor Vehicle Accidents",
    "Truck Accidents",
    "Slip and Fall Injuries",
    "Criminal Defense",
  ],
};

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      <Seo
        title="About Attorney Joseph M. Mahaney | Mahaney Law"
        description="Joseph M. Mahaney is a Massachusetts trial attorney with 40+ years of experience, a former Suffolk County ADA, and former partner at McLaughlin, Richards & Mahaney, LLP. Dedicated to his clients' success."
        path="/about"
        jsonLd={attorneyJsonLd}
      />

      {/* Page header — editorial split over office backdrop */}
      <section data-testid="about-hero" className="relative overflow-hidden bg-navy-950 md:flex md:items-center md:min-h-[calc(100vh-6rem)]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.55]"
          style={{ backgroundImage: `url(${IMAGES.hero_attorney})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/45" />
        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-gold">
                  Attorney Profile • {FIRM.tagline}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.05] tracking-tight text-white mt-5 text-balance">
                  Joseph M.
                  <span className="block italic font-medium text-gold">
                    Mahaney
                  </span>
                </h1>
                <p className="mt-7 max-w-xl text-lg md:text-[1.15rem] text-white/70 leading-relaxed font-light">
                  A Massachusetts trial attorney with 40+ years of litigation
                  experience — dedicated to his clients' success in personal
                  injury and criminal defense matters with depth and discipline.
                </p>
                <div className="divider-gold mt-8" />
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="relative rounded-sm overflow-hidden aspect-[4/5] bg-navy-900 border-2 border-gold shadow-[0_40px_90px_-40px_rgba(0,0,0,0.85)]">
                  <img
                    src={IMAGES.headshot}
                    alt="Attorney Joseph M. Mahaney"
                    className="w-full h-full object-cover object-top"
                    data-testid="about-attorney-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                    <div className="eyebrow text-gold">Massachusetts</div>
                    <div className="font-display text-2xl mt-2">Trial Counsel</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Biography"
            title="A career spent on the courtroom floor."
            testId="biography"
          />
          <Reveal delay={0.1}>
            <div className="mt-10 text-navy-900/70 leading-relaxed font-light text-lg">
              <p>
                Joseph M. Mahaney is a Massachusetts trial attorney with 40+ years
                of litigation experience in both criminal and civil matters. He
                has represented clients in Massachusetts State Courts, Federal
                Courts, the United States Court of Appeals for the First Circuit,
                and the Supreme Court of the United States.
              </p>
              <p className="mt-5">
                He previously served as an Assistant District Attorney with the
                Suffolk County District Attorney's Office and spent 17 years as a
                partner at McLaughlin, Richards &amp; Mahaney, LLP.
              </p>
              <p className="mt-5">
                Today, Attorney Mahaney leads Mahaney Law — a focused solo trial
                practice built on direct attorney-to-client representation and a
                steadfast commitment to his clients' success.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Credentials grid */}
      <section className="py-20 md:py-28 bg-navy-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Credentials"
            title="Education, admissions & experience."
            testId="credentials"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: GraduationCap, label: "Education", items: EDUCATION },
              { icon: ScrollText, label: "Bar Admissions", items: BAR_ADMISSIONS },
              {
                icon: Building,
                label: "Professional Experience",
                items: [
                  "Mahaney Law — Founder & Principal",
                  "McLaughlin, Richards & Mahaney, LLP — Partner (17 years)",
                  "Suffolk County District Attorney — Former Assistant DA",
                ],
              },
              {
                icon: Scale,
                label: "Courtroom Experience",
                items: [
                  "Massachusetts State Court trials",
                  "U.S. Federal Court litigation",
                  "U.S. Court of Appeals, First Circuit",
                  "Supreme Court of the United States",
                ],
              },
              {
                icon: Users,
                label: "Areas of Focus",
                items: [
                  "Personal injury & motor vehicle accidents",
                  "Truck accidents & slip and fall injuries",
                  "Criminal defense",
                ],
              },
              {
                icon: Award,
                label: "Experience",
                items: [
                  "40+ years of trial practice",
                  "Both criminal and civil matters",
                  "Federal & state court trial counsel",
                ],
              },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`credential-card-${i}`}
                  className="rounded-sm border border-navy-900/10 bg-white p-7 h-full hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_30px_60px_-30px_rgba(10,25,47,0.25)] transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-sm bg-gold/10 grid place-items-center">
                    <c.icon className="h-5 w-5 text-gold" />
                  </div>
                  <h3 className="font-display text-xl text-navy-900 mt-6">
                    {c.label}
                  </h3>
                  <ul className="mt-5 space-y-3 text-sm text-navy-900/65 leading-relaxed">
                    {c.items.map((it, k) => (
                      <li key={k} className="flex items-start gap-2.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Career Timeline"
            title="Four decades of practice — at a glance."
            testId="timeline"
          />
          <div className="mt-14 relative">
            <div className="absolute left-3 md:left-1/2 top-2 bottom-2 w-px bg-navy-900/15" />
            <div className="space-y-12">
              {CAREER_TIMELINE.map((t, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div
                    data-testid={`timeline-item-${i}`}
                    className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 ${
                      i % 2 === 0 ? "" : "md:[&>div:first-child]:order-2"
                    }`}
                  >
                    <div className={`pl-10 md:pl-0 ${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"}`}>
                      <div className="absolute left-0 md:left-1/2 top-1 -translate-x-1/2 h-6 w-6 rounded-full bg-gold border-4 border-white shadow" />
                      <div className="eyebrow text-gold">{t.year}</div>
                      <h3 className="font-display text-2xl text-navy-900 mt-2 leading-tight">
                        {t.title}
                      </h3>
                      <p className="mt-3 text-navy-900/65 text-sm md:text-base leading-relaxed">
                        {t.body}
                      </p>
                    </div>
                    <div />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
